import { create } from 'zustand';
import { DEFAULT_MAP_CENTER } from '@/constants/mapConstants';

export type LocationChangeReason =
  | 'init'
  | 'drag'
  | 'triple_tap'
  | 'move_to_center'
  | 'move_to_current_location'
  | 'move_home'
  | 'fallback'
  | 'address_lookup';

export interface LocationState {
  coordinates: google.maps.LatLngLiteral;
  isActualUserLocation: boolean;
  accuracy: number | null;
  isLocationReady: boolean;
  changeReason: LocationChangeReason;
}

interface LocationStore {
  location: LocationState;
  locationLoading: boolean;
  actions: {
    setLocation: (
      updates: Partial<Omit<LocationState, 'coordinates'>> & {
        coordinates?: google.maps.LatLngLiteral;
        changeReason: LocationChangeReason;
      },
    ) => void;
    setLocationLoading: (loading: boolean) => void;
  };
}

const defaultLocation: LocationState = {
  coordinates: {
    lat: DEFAULT_MAP_CENTER.lat,
    lng: DEFAULT_MAP_CENTER.lng,
  },
  isActualUserLocation: false,
  accuracy: null,
  isLocationReady: false,
  changeReason: 'init',
};

const useLocationStore = create<LocationStore>()((set) => ({
  location: defaultLocation,
  locationLoading: false,
  actions: {
    setLocation: (updates) =>
      set((state) => ({
        location: {
          ...state.location,
          ...updates,
          // If lat/lng are being set, mark location as ready
          isLocationReady:
            updates.coordinates?.lat !== undefined && updates.coordinates?.lng !== undefined ? true : state.location.isLocationReady,
        },
      })),
    setLocationLoading: (locationLoading) => set({ locationLoading }),
  },
}));

export const useLocationActions = () => useLocationStore((state) => state.actions);

export const useLocation = () => useLocationStore((state) => state.location);
export const useIsActualUserLocation = () => useLocationStore((state) => state.location.isActualUserLocation);
export const useIsLocationReady = () => useLocationStore((state) => state.location.isLocationReady);
export const useLocationLoading = () => useLocationStore((state) => state.locationLoading);
